import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useAsu, RangeType, IAnalyticsParams } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";
import { useUser } from "@framework/provider-user";
import { UserVersion } from "@framework/types";

import { StyledFormSelect } from "../../../../ui/form-select";
import {
  ANALYTICS_OBJECT,
  ANALYTICS_MEASURE_TYPE,
  ANALYTICS_VIEW,
  IGetCommonParametersArgs,
  IMechanismAutocompleteResponse,
  IComponentAutocompleteResponse,
  IAutocompletePointResponse,
  IPointAutocompleteResponse,
  IKeyphasorAutocompleteResponse,
  IParameterAutocompleteResponse,
  IMeasureAutocompleteResponse,
  IObjectOption,
  IParameterOption,
} from "../../interfaces";
import {
  idObjectName,
  idObjectsOptionsName,
  idParameterName,
  idParametersOptionsName,
  objectName,
  measureTypeName,
} from "../../constants";
import { PARAMETER_TYPE } from "../table/interfaces";
import { StyledSelectsRow } from "./styled";
import {
  findPendingPointOption,
  isPendingPointRequest,
  shouldLoadPointParametersImmediately,
} from "./pending-selection";
import { createLatestRequestGuard } from "./latest-request";

interface ISelects {
  loading?: boolean;
  view: ANALYTICS_VIEW;
}

export const Selects: FC<ISelects> = ({ loading = false, view }) => {
  const normalizeResponseArray = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : []);

  const user = useUser<any>();
  const isProfessionalVersion = user.profile.version === UserVersion.PROFESSIONAL;
  const hasAdvancedFeaturesAccess = user.profile.version === UserVersion.ADVANCED || isProfessionalVersion;
  const {
    selectedItem,
    updateQS,
    setIsAmplitudePhaseSelected,
    resetRecorderState,
    triggerRecorderReset,
    userSelectedRangeType,
    pendingAnalyticsParams,
    setPendingAnalyticsParams,
  } = useAsu();
  const [searchParams] = useSearchParams();
  const pendingIdParameterQueryParam = searchParams.get(idParameterName);

  const prevObjectRef = useRef<number | null>(null);
  const prevIdObjectRef = useRef<number | null>(null);
  const isLoadingObjectOptionsRef = useRef<boolean>(false);
  const pendingAnalyticsParamsRef = useRef<IAnalyticsParams | null>(null);
  const latestSelectionRequestRef = useRef(createLatestRequestGuard());

  useEffect(() => {
    pendingAnalyticsParamsRef.current = pendingAnalyticsParams;
  }, [pendingAnalyticsParams]);

  const clearPendingAnalyticsParams = useCallback(
    (expectedPendingParams: IAnalyticsParams | null): void => {
      if (!expectedPendingParams || pendingAnalyticsParamsRef.current !== expectedPendingParams) {
        return;
      }

      pendingAnalyticsParamsRef.current = null;
      setPendingAnalyticsParams(current => (current === expectedPendingParams ? null : current));
    },
    [setPendingAnalyticsParams],
  );

  const idTrain = useMemo(() => selectedItem && "TrainId" in selectedItem && selectedItem.TrainId, [selectedItem]);
  const trainName = useMemo(
    () => selectedItem && "TrainName" in selectedItem && "TrainId" in selectedItem && selectedItem.TrainName,
    [selectedItem],
  );

  const form = useFormContext();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const measureType = useWatch({ name: measureTypeName });
  const object = useWatch({ name: objectName });
  const idObject = useWatch({ name: idObjectName });
  const idParameter = useWatch({ name: idParameterName });
  const idObjectsOptions = useWatch({ name: idObjectsOptionsName }) as IObjectOption[];
  const idParametersOptions = useWatch({ name: idParametersOptionsName }) as IParameterOption[];

  const isTrainSelected = object === ANALYTICS_OBJECT.TRAIN;
  const isMechanismsSelected = object === ANALYTICS_OBJECT.MECHANISM;
  const isComponentsSelected = object === ANALYTICS_OBJECT.NODE;
  const isPointsSelected = object === ANALYTICS_OBJECT.POINT;

  useEffect(() => {
    latestSelectionRequestRef.current.invalidate();
  }, [measureType, object, idTrain]);

  const allowedMeasureTypes = useMemo<ANALYTICS_MEASURE_TYPE[]>(() => {
    if (isProfessionalVersion) {
      return [
        ANALYTICS_MEASURE_TYPE.TREND,
        ANALYTICS_MEASURE_TYPE.SPECTRUM,
        ANALYTICS_MEASURE_TYPE.SIGNAL_FORM,
        ANALYTICS_MEASURE_TYPE.ORBIT,
      ];
    }

    if (hasAdvancedFeaturesAccess) {
      return [ANALYTICS_MEASURE_TYPE.TREND, ANALYTICS_MEASURE_TYPE.SPECTRUM, ANALYTICS_MEASURE_TYPE.SIGNAL_FORM];
    }

    return [ANALYTICS_MEASURE_TYPE.TREND];
  }, [hasAdvancedFeaturesAccess, isProfessionalVersion]);

  const measureTypeOptions = useMemo(() => {
    const allOptions = [
      { id: ANALYTICS_MEASURE_TYPE.TREND, title: formatMessage({ id: "enums.measureType.TREND" }) },
      { id: ANALYTICS_MEASURE_TYPE.SPECTRUM, title: formatMessage({ id: "enums.measureType.SPECTRUM" }) },
      { id: ANALYTICS_MEASURE_TYPE.SIGNAL_FORM, title: formatMessage({ id: "enums.measureType.SIGNAL_FORM" }) },
      { id: ANALYTICS_MEASURE_TYPE.ORBIT, title: formatMessage({ id: "enums.measureType.ORBIT" }) },
    ];

    return allOptions.filter(({ id }) => allowedMeasureTypes.includes(id));
  }, [allowedMeasureTypes, formatMessage]);

  const objectOptions = useMemo(() => {
    const options = [
      { id: ANALYTICS_OBJECT.TRAIN, title: formatMessage({ id: "enums.object.TRAIN" }) },
      { id: ANALYTICS_OBJECT.MECHANISM, title: formatMessage({ id: "enums.object.MECHANISM" }) },
      { id: ANALYTICS_OBJECT.NODE, title: formatMessage({ id: "enums.object.NODE" }) },
      { id: ANALYTICS_OBJECT.POINT, title: formatMessage({ id: "enums.object.POINT" }) },
    ];

    return options;
  }, [formatMessage]);

  const { fn: getMechanisms, isLoading: isMechanismsLoading } = useApiCall(
    (api, { idTrain }: { idTrain: number }) => {
      isLoadingObjectOptionsRef.current = true;
      const requestId = latestSelectionRequestRef.current.begin();
      return (
        api.fetchJson({
          url: "/mechanism/autocomplete",
          method: "GET",
          data: {
            idTrain,
          },
        }) as Promise<IMechanismAutocompleteResponse[] | null | undefined>
      )
        .then((json: IMechanismAutocompleteResponse[] | null | undefined) => {
          if (!latestSelectionRequestRef.current.isLatest(requestId)) {
            return;
          }

          const mechanisms = normalizeResponseArray(json);

          if (!mechanisms.length) {
            enqueueSnackbar(formatMessage({ id: "snackbar.mechanismsNotFound" }), { variant: "error" });
            return;
          }

          const transformedJson = mechanisms
            .map(({ id, title }) => ({
              id,
              title,
              hint: `${id}`,
            }))
            .sort((a, b) => +a.id - +b.id);

          form.setValue(idObjectsOptionsName, transformedJson, { shouldDirty: true });

          const idObjectQueryParam = searchParams.get(idObjectName);
          let foundMechanism;

          const pendingParams = pendingAnalyticsParamsRef.current;

          if (pendingParams?.object === ANALYTICS_OBJECT.MECHANISM) {
            foundMechanism = transformedJson.find(({ title }) => title === pendingParams.objectTitle);
          } else if (idObjectQueryParam && isMechanismsSelected) {
            foundMechanism = transformedJson.find(({ id }) => id === +idObjectQueryParam);
          }

          const resolvedId = foundMechanism?.id ?? transformedJson[0]?.id;
          if (resolvedId) {
            form.setValue(idObjectName, resolvedId, { shouldDirty: true });
          }

          return resolvedId;
        })
        .finally(() => {
          if (latestSelectionRequestRef.current.isLatest(requestId)) {
            isLoadingObjectOptionsRef.current = false;
          }
        });
    },
    { success: false },
  );

  const { fn: getComponents, isLoading: isComponentsLoading } = useApiCall(
    (api, { idTrain }: { idTrain: number }) => {
      isLoadingObjectOptionsRef.current = true;
      const requestId = latestSelectionRequestRef.current.begin();
      return (
        api.fetchJson({
          url: "/component/autocomplete",
          method: "GET",
          data: {
            idTrain,
          },
        }) as Promise<IComponentAutocompleteResponse[] | null | undefined>
      )
        .then((json: IComponentAutocompleteResponse[] | null | undefined) => {
          if (!latestSelectionRequestRef.current.isLatest(requestId)) {
            return;
          }

          const components = normalizeResponseArray(json);

          if (!components.length) {
            enqueueSnackbar(formatMessage({ id: "snackbar.componentsNotFound" }), { variant: "error" });
            return;
          }

          const transformedJson = components
            .map(({ MechanismName, ...rest }) => ({
              ...rest,
              hint: MechanismName,
            }))
            .sort((a, b) => +a.id - +b.id);

          form.setValue(idObjectsOptionsName, transformedJson, { shouldDirty: true });

          const idObjectQueryParam = searchParams.get(idObjectName);

          let foundComponent;

          const pendingParams = pendingAnalyticsParamsRef.current;

          if (pendingParams?.object === ANALYTICS_OBJECT.NODE) {
            foundComponent = transformedJson.find(({ title }) => title === pendingParams.objectTitle);
          } else if (idObjectQueryParam && isComponentsSelected) {
            foundComponent = transformedJson.find(({ id }) => id === +idObjectQueryParam);
          }

          const resolvedId = foundComponent?.id ?? transformedJson[0]?.id;
          if (resolvedId) {
            form.setValue(idObjectName, resolvedId, { shouldDirty: true });
          }

          return resolvedId;
        })
        .finally(() => {
          if (latestSelectionRequestRef.current.isLatest(requestId)) {
            isLoadingObjectOptionsRef.current = false;
          }
        });
    },
    { success: false },
  );

  const { fn: getPoints, isLoading: isPointsLoading } = useApiCall(
    (api, { idTrain }: { idTrain: number }) => {
      isLoadingObjectOptionsRef.current = true;
      const expectedPendingParams = pendingAnalyticsParamsRef.current;
      const requestId = latestSelectionRequestRef.current.begin();

      return (
        api.fetchJson({
          url: "/point/train/autocomplete",
          method: "GET",
          data: {
            idTrain,
          },
        }) as Promise<IPointAutocompleteResponse[] | null | undefined>
      )
        .then((json: IPointAutocompleteResponse[] | null | undefined) => {
          if (!latestSelectionRequestRef.current.isLatest(requestId)) {
            return;
          }

          const points = normalizeResponseArray(json);
          const pendingParams =
            pendingAnalyticsParamsRef.current === expectedPendingParams ? expectedPendingParams : null;

          if (expectedPendingParams?.object === ANALYTICS_OBJECT.POINT && !pendingParams) {
            return;
          }

          if (!points.length) {
            if (pendingParams?.object === ANALYTICS_OBJECT.POINT) {
              clearPendingAnalyticsParams(pendingParams);
            }
            return;
          }

          let transformedJson: {
            id: string | number;
            title: string;
            hint?: string;
            points?: IPointAutocompleteResponse[];
          }[] = [];

          if (measureType === ANALYTICS_MEASURE_TYPE.ORBIT && object === ANALYTICS_OBJECT.POINT) {
            const groups: Record<string, IPointAutocompleteResponse[]> = {};
            points.forEach(point => {
              const baseName = point.title.trim();
              if (!groups[baseName]) groups[baseName] = [];
              groups[baseName].push(point);
            });

            transformedJson = Object.entries(groups)
              .filter(([_, group]) => group.length >= 2)
              .map(([baseName, group]) => ({
                id: group[0].id,
                title: baseName,
                points: group,
              }));
          } else {
            transformedJson = points
              .map(({ id, title, Direction }) => {
                return {
                  id,
                  title: `${title}${Direction ? ` ${formatMessage({ id: `enums.pointDirection.${Direction}` })}` : ""}`,
                  hint: `${id}`,
                };
              })
              .sort((a, b) => +a.id - +b.id);
          }

          form.setValue(idObjectsOptionsName, transformedJson, { shouldDirty: true });

          const idObjectQueryParam = searchParams.get(idObjectName);
          let foundPoint;

          if (pendingParams?.object === ANALYTICS_OBJECT.POINT) {
            foundPoint = findPendingPointOption(transformedJson, pendingParams);
          } else if (idObjectQueryParam && isPointsSelected) {
            foundPoint = transformedJson.find(({ id }) => id === +idObjectQueryParam);
          }

          const resolvedId =
            pendingParams?.object === ANALYTICS_OBJECT.POINT
              ? foundPoint?.id
              : foundPoint?.id ?? transformedJson[0]?.id;
          if (resolvedId !== undefined && resolvedId !== null) {
            form.setValue(idObjectName, resolvedId, { shouldDirty: true });
          } else if (pendingParams?.object === ANALYTICS_OBJECT.POINT) {
            clearPendingAnalyticsParams(pendingParams);
          }

          return resolvedId;
        })
        .catch(error => {
          if (
            latestSelectionRequestRef.current.isLatest(requestId) &&
            expectedPendingParams?.object === ANALYTICS_OBJECT.POINT
          ) {
            clearPendingAnalyticsParams(expectedPendingParams);
          }
          throw error;
        })
        .finally(() => {
          if (latestSelectionRequestRef.current.isLatest(requestId)) {
            isLoadingObjectOptionsRef.current = false;
          }
        });
    },
    { success: false },
  );

  const { fn: getCommonParameters, isLoading: isCommonParametersLoading } = useApiCall(
    (api, { keyphasorsUrl, parametersUrl, pointsUrl, data }: IGetCommonParametersArgs) => {
      const expectedPendingParams = pendingAnalyticsParamsRef.current;
      const requestId = latestSelectionRequestRef.current.begin();

      return Promise.all([
        api.fetchJson({
          url: keyphasorsUrl,
          method: "GET",
          data,
        }) as Promise<IKeyphasorAutocompleteResponse[] | null | undefined>,
        api.fetchJson({
          url: parametersUrl,
          method: "GET",
          data,
        }) as Promise<IParameterAutocompleteResponse[] | null | undefined>,
        api
          .fetchJson({
            url: pointsUrl,
            method: "GET",
            data,
          })
          .then((json: IAutocompletePointResponse[] | null | undefined) => {
            if (!latestSelectionRequestRef.current.isLatest(requestId)) {
              return [];
            }

            const points = normalizeResponseArray(json);

            if (!points.length) {
              return [];
            }

            return api.fetchJson({
              url: `/point-v2/measure/autocomplete`,
              method: "GET",
              data: {
                measureType,
                points: JSON.stringify(points.map(({ id }) => id)),
              },
            }) as Promise<IMeasureAutocompleteResponse[] | null | undefined>;
          }),
      ])
        .then(([keyphasorsResponse, parametersResponse, measuresResponse]) => {
          if (!latestSelectionRequestRef.current.isLatest(requestId)) {
            return;
          }

          const keyphasors = normalizeResponseArray(keyphasorsResponse);
          const parameters = normalizeResponseArray(parametersResponse);
          const measures = normalizeResponseArray(measuresResponse);
          const pendingParams =
            pendingAnalyticsParamsRef.current === expectedPendingParams ? expectedPendingParams : null;

          if (expectedPendingParams?.object === ANALYTICS_OBJECT.TRAIN && !pendingParams) {
            return;
          }

          if (!keyphasors?.length && !parameters?.length && !measures?.length) {
            enqueueSnackbar(formatMessage({ id: "snackbar.parametersNotFound" }), { variant: "error" });
            return;
          }

          const transformedKeyphasors = keyphasors.map(({ id, description, title }) => ({
            id: `keyphasor_${id}`,
            title: description || title,
            group: PARAMETER_TYPE.keyphasor,
          }));

          const transformedParameters = parameters.map(({ id, description, title }) => ({
            id: `parameter_${id}`,
            title: description || title,
            group: PARAMETER_TYPE.parameter,
          }));

          const transformedMeasures = measures.map(measure => {
            const id = measure.title;
            const titleText = measure.title || measure.description;
            return {
              id,
              title: titleText,
              useValue2: measure.useValue2,
              group: PARAMETER_TYPE.point,
            };
          });

          let transformedJson = [...transformedKeyphasors, ...transformedParameters, ...transformedMeasures].filter(
            ({ id, title }) => id !== null && title !== null,
          );

          if (measureType !== ANALYTICS_MEASURE_TYPE.TREND) {
            transformedJson = transformedJson.filter(({ group }) => group === PARAMETER_TYPE.point);
          }

          form.setValue(idParametersOptionsName, transformedJson, { shouldDirty: true });

          const idParameterQueryParam = searchParams.get(idParameterName);
          let foundParameter;

          if (pendingParams?.object === object && pendingParams?.parameterTitle) {
            foundParameter = transformedJson.find(({ id }) => id === pendingParams.parameterTitle);
          }

          if (!foundParameter && idParameterQueryParam) {
            foundParameter = transformedJson.find(({ id }) => String(id) === idParameterQueryParam);
          }

          if (foundParameter) {
            form.setValue(idParameterName, foundParameter.id, { shouldDirty: true });
          } else {
            transformedJson[0]?.id && form.setValue(idParameterName, transformedJson[0].id, { shouldDirty: true });
          }
        })
        .finally(() => {
          if (
            latestSelectionRequestRef.current.isLatest(requestId) &&
            expectedPendingParams?.object === ANALYTICS_OBJECT.TRAIN
          ) {
            clearPendingAnalyticsParams(expectedPendingParams);
          }
        });
    },
    { success: false },
  );

  const getTrainParameters = (idTrain: number): Promise<void> => {
    return getCommonParameters(form, {
      data: { idTrain },
      keyphasorsUrl: `/trains/${idTrain}/keyphasors`,
      parametersUrl: `/trains/${idTrain}/parameters`,
      pointsUrl: `/point/train/autocomplete`,
    });
  };

  const getMechanismParameters = (idTrainMech: number): Promise<void> => {
    return getCommonParameters(form, {
      data: { idTrainMech },
      keyphasorsUrl: `/mechanism/keyphasor/autocomplete`,
      parametersUrl: `/mechanism/parameter/autocomplete`,
      pointsUrl: `/mechanism/point/autocomplete`,
    });
  };

  const getComponentParameters = (idTrainComponent: number): Promise<void> => {
    return getCommonParameters(form, {
      data: { idTrainComponent },
      keyphasorsUrl: `/component/keyphasor/autocomplete`,
      parametersUrl: `/component/parameter/autocomplete`,
      pointsUrl: `/component/point/autocomplete`,
    });
  };

  const { fn: getPointParameters, isLoading: isPointParametersLoading } = useApiCall(
    (api, { idPoint }: { idPoint: number }) => {
      const expectedPendingParams = pendingAnalyticsParamsRef.current;
      const requestId = latestSelectionRequestRef.current.begin();

      return (
        api.fetchJson({
          url: `/point-v2/measure/autocomplete`,
          method: "GET",
          data: {
            measureType,
            points: JSON.stringify([idPoint]),
          },
        }) as Promise<IMeasureAutocompleteResponse[] | null | undefined>
      )
        .then((measures: IMeasureAutocompleteResponse[] | null | undefined) => {
          if (!latestSelectionRequestRef.current.isLatest(requestId)) {
            return;
          }

          const normalizedMeasures = normalizeResponseArray(measures);
          const pendingParams =
            pendingAnalyticsParamsRef.current === expectedPendingParams ? expectedPendingParams : null;
          const matchingPendingParams = isPendingPointRequest(pendingParams, idPoint) ? pendingParams : null;

          if (!normalizedMeasures.length) {
            return;
          }

          const transformedMeasures = normalizedMeasures.map(measure => {
            const id = measure.title;
            const titleText =
              measure.description && measure.description !== measure.title
                ? `${measure.description}  (${measure.title})`
                : measure.title;
            return {
              id,
              title: titleText,
              useValue2: measure.useValue2,
              group: PARAMETER_TYPE.point,
            };
          });

          const transformedJson = [...transformedMeasures].filter(({ id, title }) => id !== null && title !== null);

          form.setValue(idParametersOptionsName, transformedJson, { shouldDirty: true });

          if (matchingPendingParams?.selectAllParameters) {
            const allIds = transformedJson.map(option => option.id);
            form.setValue(idParameterName, allIds, { shouldDirty: true });
            return;
          }

          let foundParameter;

          if (matchingPendingParams?.parameterTitle) {
            foundParameter = transformedJson.find(({ id }) => `${id}` === matchingPendingParams.parameterTitle);
          }
          if (!foundParameter) {
            const idParameterQueryParam = searchParams.get(idParameterName);

            if (idParameterQueryParam && isPointsSelected) {
              foundParameter = transformedJson.find(({ id }) => `${id}` === idParameterQueryParam);
            }
          }

          if (foundParameter) {
            form.setValue(idParameterName, foundParameter.id, { shouldDirty: true });
          } else {
            transformedJson[0]?.id && form.setValue(idParameterName, transformedJson[0].id, { shouldDirty: true });
          }
        })
        .finally(() => {
          if (
            latestSelectionRequestRef.current.isLatest(requestId) &&
            isPendingPointRequest(expectedPendingParams, idPoint)
          ) {
            clearPendingAnalyticsParams(expectedPendingParams);
          }
        });
    },
    { success: false },
  );

  const isAmplitudePhaseSelected =
    (Array.isArray(idParameter) &&
      idParameter.length > 0 &&
      idParameter.every(id => idParametersOptions.find(p => p.id === id)?.useValue2 === true)) ||
    (!Array.isArray(idParameter) &&
      idParameter &&
      idParametersOptions.find(p => p.id === idParameter)?.useValue2 === true);

  const hasAmplitudePhase = Array.isArray(idParameter)
    ? idParameter.some(id => idParametersOptions.find(p => p.id === id)?.useValue2 === true)
    : idParameter && idParametersOptions.find(p => p.id === idParameter)?.useValue2 === true;

  const hasNonAmplitudePhase = Array.isArray(idParameter)
    ? idParameter.some(id => idParametersOptions.find(p => p.id === id)?.useValue2 !== true)
    : idParameter && idParametersOptions.find(p => p.id === idParameter)?.useValue2 !== true;

  const disabledOptions = idParametersOptions
    .filter(option => {
      if (hasNonAmplitudePhase && option.useValue2 === true) return true;
      if (hasAmplitudePhase && option.useValue2 !== true) return true;
      return false;
    })
    .map(option => option.id);

  const resetObjects = useCallback(() => {
    form.setValue(idObjectName, null, { shouldDirty: false });
    form.setValue(idObjectsOptionsName, [], { shouldDirty: false });
  }, [form]);

  const resetParameters = useCallback(() => {
    form.setValue(idParameterName, null, { shouldDirty: false });
    form.setValue(idParametersOptionsName, [], { shouldDirty: false });
  }, [form]);

  const handleRecorderSelectChange = useCallback((): Promise<void> => {
    if (userSelectedRangeType !== RangeType.RECORDER_MODE) {
      return Promise.resolve();
    }

    resetRecorderState();
    triggerRecorderReset();

    return Promise.resolve();
  }, [resetRecorderState, triggerRecorderReset, userSelectedRangeType]);

  const handleSelectChange = useCallback((): Promise<void> => {
    latestSelectionRequestRef.current.invalidate();
    clearPendingAnalyticsParams(pendingAnalyticsParamsRef.current);
    return handleRecorderSelectChange();
  }, [clearPendingAnalyticsParams, handleRecorderSelectChange]);

  const isLoading =
    loading ||
    isMechanismsLoading ||
    isPointsLoading ||
    isComponentsLoading ||
    isCommonParametersLoading ||
    isPointParametersLoading;

  useEffect(() => {
    if (!pendingAnalyticsParams) return;

    latestSelectionRequestRef.current.invalidate();

    const currentMeasureType = form.getValues(measureTypeName);
    const currentObject = form.getValues(objectName);

    const measureTypeUnchanged = currentMeasureType === pendingAnalyticsParams.measureType;
    const objectUnchanged = currentObject === pendingAnalyticsParams.object;

    form.setValue(measureTypeName, pendingAnalyticsParams.measureType, { shouldDirty: true });
    form.setValue(objectName, pendingAnalyticsParams.object, { shouldDirty: true });

    if (measureTypeUnchanged && objectUnchanged && idTrain) {
      switch (pendingAnalyticsParams.object as ANALYTICS_OBJECT) {
        case ANALYTICS_OBJECT.TRAIN:
          form.setValue(idObjectsOptionsName, [{ id: idTrain, title: trainName }], { shouldDirty: false });
          form.setValue(idObjectName, idTrain, { shouldDirty: false });
          void getTrainParameters(idTrain);
          break;
        case ANALYTICS_OBJECT.MECHANISM:
          void getMechanisms(form, { idTrain }).then(id => id && getMechanismParameters(id));
          break;
        case ANALYTICS_OBJECT.NODE:
          void getComponents(form, { idTrain }).then(id => id && getComponentParameters(id));
          break;
        case ANALYTICS_OBJECT.POINT: {
          const currentIdObject = form.getValues(idObjectName);
          void getPoints(form, { idTrain }).then(id => {
            if (typeof id !== "string" && typeof id !== "number") {
              return undefined;
            }

            if (shouldLoadPointParametersImmediately(currentIdObject, id)) {
              return getPointParameters(form, { idPoint: +id });
            }

            return undefined;
          });
          break;
        }
      }
    }
  }, [pendingAnalyticsParams]);

  useEffect(() => {
    resetObjects();
    resetParameters();
    prevObjectRef.current = null;
    prevIdObjectRef.current = null;
  }, [measureType, object, idTrain]);

  useEffect(() => {
    const normalizedMeasureType = Number(measureType) as ANALYTICS_MEASURE_TYPE;
    if (Number.isNaN(normalizedMeasureType)) {
      return;
    }

    if (!allowedMeasureTypes.includes(normalizedMeasureType)) {
      form.setValue(measureTypeName, ANALYTICS_MEASURE_TYPE.TREND, { shouldDirty: true });
    }
  }, [allowedMeasureTypes, form, measureType]);

  useEffect(() => {
    if (!idTrain) return;

    switch (object) {
      case ANALYTICS_OBJECT.TRAIN:
        form.setValue(idObjectsOptionsName, [{ id: idTrain, title: trainName }], { shouldDirty: false });
        form.setValue(idObjectName, idTrain, { shouldDirty: false });
        break;
      case ANALYTICS_OBJECT.MECHANISM:
        void getMechanisms(form, { idTrain });
        break;
      case ANALYTICS_OBJECT.NODE:
        void getComponents(form, { idTrain });
        break;
      case ANALYTICS_OBJECT.POINT:
        void getPoints(form, { idTrain });
        break;
    }
  }, [measureType, object, idTrain]);

  useEffect(() => {
    if (isLoadingObjectOptionsRef.current) return;

    if (object !== prevObjectRef.current && prevObjectRef.current !== null) {
      prevObjectRef.current = object;
      prevIdObjectRef.current = null;
      return;
    }

    if (prevObjectRef.current === null) {
      prevObjectRef.current = object;
    }

    if (object === ANALYTICS_OBJECT.TRAIN) {
      if (!idTrain) return;

      if (idObject !== idTrain) {
        prevIdObjectRef.current = idObject;
        return;
      }

      if (prevIdObjectRef.current === idTrain) return;

      prevIdObjectRef.current = idTrain;
      void getTrainParameters(idTrain);
      return;
    }

    if (!idObject) return;

    if (idObject === prevIdObjectRef.current) return;

    prevIdObjectRef.current = idObject;

    switch (object) {
      case ANALYTICS_OBJECT.MECHANISM:
        void getMechanismParameters(idObject);
        break;
      case ANALYTICS_OBJECT.NODE:
        void getComponentParameters(idObject);
        break;
      case ANALYTICS_OBJECT.POINT:
        void getPointParameters(form, { idPoint: idObject });
        break;
    }
  }, [measureType, object, idObject, idTrain]);

  useEffect(() => {
    const newSearchParams: Record<string, number | string | null> = {};

    newSearchParams.view = view === ANALYTICS_VIEW.CHART ? null : view;
    newSearchParams.measureType =
      measureType === null || measureType === ANALYTICS_MEASURE_TYPE.TREND ? null : measureType;
    newSearchParams.object = object === null || object === ANALYTICS_OBJECT.TRAIN ? null : object;
    newSearchParams.idObject = idObject || null;
    newSearchParams.idParameter = idParameter
      ? Array.isArray(idParameter)
        ? idParameter.join(",")
        : idParameter
      : pendingIdParameterQueryParam && idParametersOptions.length === 0
        ? pendingIdParameterQueryParam
        : null;

    updateQS(newSearchParams);
  }, [view, measureType, object, idObject, idParameter, pendingIdParameterQueryParam, idParametersOptions.length]);

  useEffect(() => {
    setIsAmplitudePhaseSelected(isAmplitudePhaseSelected);
  }, [isAmplitudePhaseSelected]);

  return (
    <ProgressOverlay isLoading={isLoading}>
      <StyledSelectsRow>
        <StyledFormSelect name={measureTypeName} options={measureTypeOptions} onChange={handleSelectChange} />
        <StyledFormSelect name={objectName} options={objectOptions} onChange={handleSelectChange} />
        <StyledFormSelect
          name={idObjectName}
          options={idObjectsOptions}
          isLoading={isLoading}
          disabled={isTrainSelected}
          onChange={handleSelectChange}
        />
        {hasAdvancedFeaturesAccess ? (
          <StyledFormSelect
            name={idParameterName}
            options={idParametersOptions}
            isGrouped
            multiple={measureType === ANALYTICS_MEASURE_TYPE.TREND}
            disabledOptions={disabledOptions}
            onChange={handleSelectChange}
          />
        ) : (
          <StyledFormSelect
            name={idParameterName}
            options={idParametersOptions}
            isGrouped
            onChange={handleSelectChange}
          />
        )}
      </StyledSelectsRow>
    </ProgressOverlay>
  );
};
